import React, { useState } from "react";
import { API_BASE_URL } from "../services/api";

// Generated by GitHub Copilot
/**
 * Returns true when the API error indicates LLM configuration is missing/unavailable.
 */
const isLlmNotConfiguredError = (message: string): boolean =>
  /llm\s+not\s+configured|llm\s+unavailable/i.test(message);

// Generated by GitHub Copilot
/**
 * Verifies LLM availability before update-description operations.
 */
const assertLlmConfiguredForUpdate = async (): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/llm/status`);
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const detail = typeof data?.detail === "string" ? data.detail : "LLM not configured";
    throw new Error(detail);
  }
};

type Mode = "auto" | "manual";
type ManualItem = { key: string; description: string };
type ManualKind = "tables" | "columns" | "schemas";

interface UpdateResult {
  ok: boolean;
  ds_id: string | number;
  generated_counts?: { tables: number; columns: number; schemas: number };
  patch?: {
    updated?: Record<string, { count: number; status_code: number }>;
  };
  updated?: Record<string, { count: number; status_code: number }>;
  logs?: string[];
  error_detail?: string;
}

const BASE_URL = API_BASE_URL;

const emptyItem = (): ManualItem => ({ key: "", description: "" });

const UpdateDescriptionPage: React.FC = () => {
  const [mode, setMode]       = useState<Mode>("auto");
  const [dsId, setDsId]       = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult]   = useState<UpdateResult | null>(null);
  const [error, setError]     = useState<string | null>(null);

  // Manual mode rows
  const [tables,  setTables]  = useState<ManualItem[]>([emptyItem()]);
  const [columns, setColumns] = useState<ManualItem[]>([emptyItem()]);
  const [schemas, setSchemas] = useState<ManualItem[]>([emptyItem()]);

  // ── helpers ──────────────────────────────────────────────────────────────
  const updateRow = (
    setter: React.Dispatch<React.SetStateAction<ManualItem[]>>,
    idx: number,
    field: keyof ManualItem,
    value: string
  ) => setter(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));

  const addRow    = (setter: React.Dispatch<React.SetStateAction<ManualItem[]>>) =>
    setter(prev => [...prev, emptyItem()]);

  const removeRow = (setter: React.Dispatch<React.SetStateAction<ManualItem[]>>, idx: number) =>
    setter(prev => prev.filter((_, i) => i !== idx));

  const validRows = (rows: ManualItem[]) => rows.filter(r => r.key.trim() && r.description.trim());

  // ── submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!dsId.trim()) { setError("Please enter a Data Source ID."); return; }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      await assertLlmConfiguredForUpdate();

      let body: Record<string, unknown> = { ds_id: dsId.trim() };

      if (mode === "auto") {
        body.auto_generate = true;
      } else {
        const t = validRows(tables);
        const c = validRows(columns);
        const s = validRows(schemas);
        if (!t.length && !c.length && !s.length) {
          setError("Please fill in at least one row with a Key and Description.");
          setLoading(false);
          return;
        }
        if (t.length) body.tables  = t;
        if (c.length) body.columns = c;
        if (s.length) body.schemas = s;
      }

      const resp = await fetch(`${BASE_URL}/alation/update-description`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await resp.json().catch(() => ({}));

      if (!resp.ok) {
        setError(data.detail || `Server error: ${resp.status}`);
        return;
      }
      setResult(data);
    } catch (err: any) {
      const message = err?.message || "Please try again.";
      const displayError = "Request failed: " + message;
      setError(displayError);
      if (isLlmNotConfiguredError(message)) {
        alert(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setResult(null);
    setError(null);
    setTables([emptyItem()]);
    setColumns([emptyItem()]);
    setSchemas([emptyItem()]);
  };

  // ── result helpers ────────────────────────────────────────────────────────
  const updatedMap: Record<string, { count: number; status_code: number }> =
    result?.patch?.updated || result?.updated || {};

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div style={styles.container}>
      <h2 style={styles.title}>✏️ Update Alation Descriptions</h2>
      <p style={styles.subtitle}>
        Patch table, column, and schema descriptions directly in your Alation data source.
      </p>

      {/* ── DS ID input ── */}
      <div style={styles.card}>
        <label style={styles.label}>Alation Data Source ID</label>
        <input
          style={styles.input}
          type="text"
          value={dsId}
          onChange={e => setDsId(e.target.value)}
          placeholder={`e.g. ${process.env.REACT_APP_DATASOURCE_ID || "11"}`}
          disabled={loading}
        />

        {/* ── Mode toggle ── */}
        <div style={styles.modeToggleRow}>
          <button
            style={mode === "auto" ? styles.modeActive : styles.modeBtn}
            onClick={() => setMode("auto")}
            disabled={loading}
          >
            🤖 Auto-Generate (AI fills missing descriptions)
          </button>
          <button
            style={mode === "manual" ? styles.modeActive : styles.modeBtn}
            onClick={() => setMode("manual")}
            disabled={loading}
          >
            ✍️ Manual (provide specific keys + descriptions)
          </button>
        </div>

        {/* ── auto mode info ── */}
        {mode === "auto" && (
          <div style={styles.infoBox}>
            <strong>Auto mode:</strong> The AI will fetch all tables, columns and schemas
            from the selected data source, generate descriptions for any that are currently
            empty, and push them back to Alation automatically. This may take a minute.
          </div>
        )}

        {/* ── manual mode rows ── */}
        {mode === "manual" && (
          <div>
            {(["tables", "columns", "schemas"] as ManualKind[]).map(kind => {
              const rows   = kind === "tables" ? tables : kind === "columns" ? columns : schemas;
              const setter = kind === "tables" ? setTables : kind === "columns" ? setColumns : setSchemas;
              const icon   = kind === "tables" ? "🗃" : kind === "columns" ? "📊" : "🗂";
              return (
                <div key={kind} style={styles.manualSection}>
                  <div style={styles.manualSectionHeader}>
                    {icon} {kind.charAt(0).toUpperCase() + kind.slice(1)}
                    <button style={styles.addRowBtn} onClick={() => addRow(setter)} disabled={loading}>
                      + Add row
                    </button>
                  </div>
                  {rows.map((row, idx) => (
                    <div key={idx} style={styles.manualRow}>
                      <input
                        style={{ ...styles.input, ...styles.keyInput }}
                        placeholder={
                          kind === "tables"  ? "key  (e.g. 11.my_schema.my_table)" :
                          kind === "columns" ? "key  (e.g. 11.my_schema.my_table.my_column)" :
                                              "key  (e.g. 11.my_schema)"
                        }
                        value={row.key}
                        onChange={e => updateRow(setter, idx, "key", e.target.value)}
                        disabled={loading}
                      />
                      <input
                        style={{ ...styles.input, ...styles.descInput }}
                        placeholder="description"
                        value={row.description}
                        onChange={e => updateRow(setter, idx, "description", e.target.value)}
                        disabled={loading}
                      />
                      {rows.length > 1 && (
                        <button style={styles.removeBtn} onClick={() => removeRow(setter, idx)} disabled={loading}>✕</button>
                      )}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {/* ── submit ── */}
        <button
          style={{ ...styles.submitBtn, opacity: loading ? 0.7 : 1 }}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading
            ? (mode === "auto" ? "⏳ Generating & Patching..." : "⏳ Patching...")
            : (mode === "auto" ? "🚀 Run Auto-Generate & Patch" : "🚀 Patch Descriptions")}
        </button>
      </div>

      {/* ── error ── */}
      {error && (
        <div style={styles.errorBox}>
          <strong>❌ Error:</strong> {error}
          <button onClick={reset} style={styles.resetBtn}>Try Again</button>
        </div>
      )}

      {/* ── result ── */}
      {result && (
        <div style={result.ok ? styles.resultBox : styles.resultBoxFail}>
          <h3 style={{ marginTop: 0 }}>
            {result.ok ? "✅ Descriptions Updated Successfully" : "⚠️ Update Completed with Errors"}
          </h3>

          <div style={styles.metaRow}>
            <span style={styles.metaLabel}>Data Source ID:</span>
            <span>{result.ds_id}</span>
          </div>

          {result.generated_counts && (
            <div style={styles.countsRow}>
              <div style={styles.countBadge}>
                <span style={styles.countNum}>{result.generated_counts.tables}</span>
                <span style={styles.countLbl}>Tables</span>
              </div>
              <div style={styles.countBadge}>
                <span style={styles.countNum}>{result.generated_counts.columns}</span>
                <span style={styles.countLbl}>Columns</span>
              </div>
              <div style={styles.countBadge}>
                <span style={styles.countNum}>{result.generated_counts.schemas}</span>
                <span style={styles.countLbl}>Schemas</span>
              </div>
            </div>
          )}

          {Object.keys(updatedMap).length > 0 && (
            <div style={{ marginTop: 12 }}>
              <strong style={styles.metaLabel}>Patch Results:</strong>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Kind</th>
                    <th style={styles.th}>Records Sent</th>
                    <th style={styles.th}>HTTP Status</th>
                    <th style={styles.th}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(updatedMap).map(([kind, info]) => (
                    <tr key={kind}>
                      <td style={styles.td}>{kind}</td>
                      <td style={styles.td}>{info.count}</td>
                      <td style={styles.td}>{info.status_code}</td>
                      <td style={styles.td}>
                        <span style={info.status_code < 400 ? styles.statusOk : styles.statusFail}>
                          {info.status_code < 400 ? "✅ OK" : "❌ Failed"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {result.logs && result.logs.length > 0 && (
            <details style={{ marginTop: 16 }}>
              <summary style={{ cursor: "pointer", fontWeight: "bold", color: "#555" }}>
                📋 Processing Logs ({result.logs.length})
              </summary>
              <div style={styles.logsBox}>
                {result.logs.map((log, i) => (
                  <div key={i} style={styles.logLine}>{log}</div>
                ))}
              </div>
            </details>
          )}

          {result.error_detail && (
            <pre style={styles.errorDetail}>{result.error_detail}</pre>
          )}

          <button onClick={reset} style={styles.resetBtn}>🔄 Run Another Update</button>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container:      { maxWidth: 860, margin: "0 auto", padding: 32 },
  title:          { fontSize: 24, marginBottom: 6, color: "#333" },
  subtitle:       { fontSize: 14, color: "#666", marginBottom: 24, marginTop: 0 },

  card:           { background: "#fff", border: "1px solid #ddd", borderRadius: 10, padding: 28, marginBottom: 20 },
  label:          { fontWeight: "bold", display: "block", marginBottom: 8, color: "#555" },
  input:          { width: "100%", padding: "10px 14px", fontSize: 14, borderRadius: 6, border: "1px solid #ccc", marginBottom: 16, boxSizing: "border-box" },
  keyInput:       { width: "38%", marginRight: 8, marginBottom: 0 },
  descInput:      { width: "56%", marginBottom: 0 },

  modeToggleRow:  { display: "flex", gap: 10, marginBottom: 16 },
  modeBtn:        { flex: 1, padding: "10px 16px", background: "#f5f5f5", color: "#555", border: "1px solid #ccc", borderRadius: 8, cursor: "pointer", fontSize: 13 },
  modeActive:     { flex: 1, padding: "10px 16px", background: "#1565c0", color: "#fff", border: "1px solid #1565c0", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: "bold" },

  infoBox:        { background: "#e3f2fd", border: "1px solid #90caf9", borderRadius: 8, padding: "12px 16px", fontSize: 13, color: "#1565c0", marginBottom: 16 },

  manualSection:       { marginBottom: 20 },
  manualSectionHeader: { fontWeight: "bold", color: "#444", marginBottom: 8, display: "flex", alignItems: "center", gap: 12, fontSize: 15 },
  addRowBtn:      { padding: "4px 12px", background: "#e3f2fd", color: "#1565c0", border: "1px solid #90caf9", borderRadius: 6, cursor: "pointer", fontSize: 12 },
  manualRow:      { display: "flex", alignItems: "center", gap: 8, marginBottom: 8 },
  removeBtn:      { padding: "8px 10px", background: "#ffebee", color: "#c62828", border: "1px solid #ef9a9a", borderRadius: 6, cursor: "pointer", fontSize: 13 },

  submitBtn:      { width: "100%", padding: "13px 0", background: "#1565c0", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 16, fontWeight: "bold", marginTop: 8 },

  errorBox:       { background: "#ffebee", border: "1px solid #ef9a9a", borderRadius: 10, padding: 20, marginBottom: 20 },
  resetBtn:       { marginTop: 12, padding: "10px 22px", background: "#f57c00", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", display: "block" },

  resultBox:      { background: "#e8f5e9", border: "1px solid #a5d6a7", borderRadius: 10, padding: 24 },
  resultBoxFail:  { background: "#fff8e1", border: "1px solid #ffe082", borderRadius: 10, padding: 24 },
  metaRow:        { display: "flex", gap: 12, marginBottom: 10, alignItems: "center" },
  metaLabel:      { fontWeight: "bold", minWidth: 150, color: "#555" },

  countsRow:      { display: "flex", gap: 16, margin: "16px 0" },
  countBadge:     { background: "#fff", border: "1px solid #c8e6c9", borderRadius: 10, padding: "12px 20px", textAlign: "center", minWidth: 90 },
  countNum:       { display: "block", fontSize: 28, fontWeight: "bold", color: "#2e7d32" },
  countLbl:       { display: "block", fontSize: 12, color: "#555", marginTop: 2 },

  table:          { width: "100%", borderCollapse: "collapse", marginTop: 8, fontSize: 13 },
  th:             { background: "#f5f5f5", padding: "8px 12px", textAlign: "left", border: "1px solid #e0e0e0", fontWeight: "bold", color: "#555" },
  td:             { padding: "8px 12px", border: "1px solid #e0e0e0", color: "#333" },
  statusOk:       { color: "#2e7d32", fontWeight: "bold" },
  statusFail:     { color: "#c62828", fontWeight: "bold" },

  logsBox:        { background: "#fafafa", border: "1px solid #e0e0e0", borderRadius: 6, padding: "12px 16px", marginTop: 8, maxHeight: 200, overflowY: "auto", fontSize: 12, fontFamily: "monospace" },
  logLine:        { padding: "2px 0", borderBottom: "1px solid #f0f0f0" },
  errorDetail:    { background: "#fff3e0", border: "1px solid #ffcc80", borderRadius: 6, padding: 12, fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-all", marginTop: 12, maxHeight: 200, overflowY: "auto" },
};

export default UpdateDescriptionPage;
